import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { TenantCapabilities } from '@/shared/api/tenantSettings';
import {
  ADMIN_ITEM,
  HELP_ITEM,
  activeNavTarget,
  canSee,
  visibleSections,
  type NavItem,
} from '@/shared/navigation/manifest';

import classes from './ConsoleNavbar.module.css';
import { Icon } from './Icon';

interface ConsoleNavbarProps {
  permissions: ReadonlySet<string>;
  capabilities: TenantCapabilities;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onNavigate?: () => void;
}

/** Persistent side navigation of the console (13 §2.2, §3.1), built from the manifest. */
export function ConsoleNavbar({
  permissions,
  capabilities,
  collapsed,
  onToggleCollapsed,
  onNavigate,
}: ConsoleNavbarProps) {
  const { t } = useTranslation('nav');
  const sections = visibleSections(permissions, capabilities);

  return (
    <nav
      className={collapsed ? `${classes.navbar} ${classes.collapsed}` : classes.navbar}
      aria-label={t('aria.main')}
    >
      {sections.map((section) => (
        <div key={section.labelKey}>
          <div className={classes.sectionLabel}>{t(section.labelKey)}</div>
          {section.items.map((item) => (
            <NavEntry key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
      <div className={classes.spacer} />
      <div className={classes.bottom}>
        {canSee(ADMIN_ITEM, permissions, capabilities) ? (
          <NavEntry item={ADMIN_ITEM} collapsed={collapsed} onNavigate={onNavigate} />
        ) : null}
        <NavEntry item={HELP_ITEM} collapsed={collapsed} onNavigate={onNavigate} />
        <button
          type="button"
          className={classes.toggle}
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
        >
          <Icon name={collapsed ? 'chevronRight' : 'chevronLeft'} />
          <span className={classes.label}>{t('action.collapse')}</span>
        </button>
      </div>
    </nav>
  );
}

function NavEntry({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const { t } = useTranslation('nav');
  const label = t(item.labelKey);
  // Only the most specific item lights up: an item with another one beneath it matches exactly
  // while the path belongs to that other item.
  const location = useLocation();
  const lit = activeNavTarget(location.pathname, location.search) === item.to;

  if (!item.implemented) {
    return (
      <span
        className={`${classes.item} ${classes.soon}`}
        title={collapsed ? `${label} · ${t('state.soon')}` : undefined}
        aria-disabled="true"
      >
        <Icon name={item.icon} />
        <span className={classes.label}>{label}</span>
        <span className={classes.soonTag}>{t('state.soon')}</span>
      </span>
    );
  }

  // An entry whose page lives at another address (`lightsAt`: the four report families redirect to
  // `/reports?family=...`) never matches the router's own `to`, so the manifest's answer lights it.
  const pinned = lit && item.lightsAt !== undefined;

  return (
    <Link
      to={item.to}
      className={pinned ? `${classes.item} ${classes.active}` : classes.item}
      activeProps={pinned ? {} : { className: `${classes.item} ${classes.active}` }}
      activeOptions={{ exact: !lit }}
      title={collapsed ? label : undefined}
      onClick={onNavigate}
    >
      <Icon name={item.icon} />
      <span className={classes.label}>{label}</span>
    </Link>
  );
}
